// frontend/src/components/CuttingPlan/SeparationPlanner.jsx

import React, { useState, useEffect } from 'react';
import { cuttingPlansAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import DataTable from '../Common/DataTable';

const SeparationPlanner = ({ sessionIp, selectedOrders, separations, onSeparationsChange }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedOrders.length > 0) {
      loadSummary();
    }
  }, [selectedOrders, sessionIp]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await cuttingPlansAPI.getSummary(sessionIp);
      
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      console.error('Error loading summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeparation = () => {
    const newSep = {
      sequence: separations.length + 1,
      fabricLayers: 1,
      lines: []
    };
    onSeparationsChange([...separations, newSep]);
  };

  const handleRemoveSeparation = (index) => {
    const updated = separations.filter((_, i) => i !== index);
    // Renumber sequences
    updated.forEach((sep, i) => sep.sequence = i + 1);
    onSeparationsChange(updated);
  };

  const handleLayersChange = (index, value) => {
    const updated = [...separations];
    updated[index].fabricLayers = parseInt(value) || 1;
    onSeparationsChange(updated);
  };

  const summaryColumns = [
    {
      header: t('cutting_plans.product', 'Product'),
      field: 'product'
    },
    {
      header: t('cutting_plans.color', 'Color'),
      field: 'color'
    },
    {
      header: t('cutting_plans.size', 'Size'),
      field: 'size'
    },
    {
      header: t('cutting_plans.total_qty', 'Total Qty'),
      field: 'total_quantity',
      className: 'text-right',
      render: (row) => row.total_quantity != null ? parseFloat(row.total_quantity).toFixed(0) : ''
    }
  ];

  return (
    <div className="separation-planner">
      <div className="section-header">
        <h3 className="section-title-medium">
          {t('cutting_plans.separations', 'Fabric Separations')}
        </h3>
        <button
          type="button"
          onClick={handleAddSeparation}
          className="btn-primary btn-sm"
        >
          <FiPlus size={14} />
          {t('cutting_plans.add_separation', 'Add Separation')}
        </button>
      </div>

      {/* Selected Orders Summary */}
      {summary.length > 0 && (
        <div className="summary-section">
          <h4>{t('cutting_plans.selected_summary', 'Selected Orders Summary')}</h4>
          <DataTable
            columns={summaryColumns}
            data={summary}
            loading={loading}
            emptyMessage={t('no_data', 'No data')}
          />
        </div>
      )}

      {/* Separations */}
      {separations.length > 0 && (
        <div className="separations-list">
          {separations.map((sep, index) => (
            <div key={index} className="separation-card">
              <div className="separation-header">
                <h4>
                  {t('cutting_plans.separation', 'Separation')} #{sep.sequence}
                </h4>
                <div className="separation-controls">
                  <label>
                    {t('cutting_plans.fabric_layers', 'Fabric Layers')}:
                    <input
                      type="number"
                      value={sep.fabricLayers}
                      onChange={(e) => handleLayersChange(index, e.target.value)}
                      min="1"
                      style={{ width: '80px', marginLeft: '8px' }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemoveSeparation(index)}
                    className="btn-action btn-delete"
                    title={t('delete', 'Delete')}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="separation-info">
                {t('cutting_plans.separation_placeholder', 'Configure separation ratios and quantities here')}
              </div>
            </div>
          ))}
        </div>
      )}

      {separations.length === 0 && (
        <div className="no-separations">
          <p>{t('cutting_plans.no_separations', 'No separations added. Click "Add Separation" to start.')}</p>
        </div>
      )}
    </div>
  );
};

export default SeparationPlanner;